import {
  audioConstraintsForDevice,
  createSegmentRecorder,
  isLikelySilent,
  selectSupportedMimeType,
} from "./media";
import { BrowserQueueStore } from "./queue-store";
import { UploadQueue } from "./upload-queue";
import {
  AudioCapture,
  type AudioCaptureSnapshot,
  type AudioFrame as CapturedAudioFrame,
} from "../audio/audio-capture";
import { PcmChunker, type RawChunk } from "../audio/audio-chunker";
import { TARGET_SAMPLE_RATE } from "../audio/audio-config";
import {
  AudioStreamingSession,
  readAudioStreamingConfig,
  type AudioStreamingSnapshot,
  type AudioStreamingStartOptions,
} from "../audio/audio-streaming-session";
import type {
  ChunkMetadata,
  CompleteSessionOperation,
  CreateEventOperation,
  CreateSessionOperation,
  QueueOperation,
  SessionEventType,
  UploadChunkOperation,
} from "../types";

const SEGMENT_DURATION_MS = 30_000;
const CALIBRATION_DURATION_MS = 3_000;
const DEFAULT_SPEECH_THRESHOLD = 0.015;
const RMS_SAMPLE_INTERVAL_MS = 100;

export type RecorderState =
  | "idle"
  | "calibrating"
  | "recording"
  | "muted"
  | "stopping"
  | "completed"
  | "microphone_disconnected"
  | "error";

export type MicrophoneState =
  | "not_requested"
  | "calibrating"
  | "live"
  | "muted"
  | "disconnected"
  | "stopped";

export interface RecorderSnapshot {
  state: RecorderState;
  microphoneState: MicrophoneState;
  sessionId?: string;
  sessionStartedAt?: number;
  segmentStartedAt?: number;
  persistedChunkCount: number;
  likelySilentCount: number;
  selectedMimeType: string;
  noiseFloor?: number;
  speechThreshold?: number;
  streaming: AudioStreamingSnapshot;
  error?: string;
}

interface SegmentMetrics {
  speechMs: number;
  peakRms: number;
  rmsWeightedTotal: number;
  rmsDurationMs: number;
  window: RmsWindow;
}

interface RmsWindow {
  durationMs: number;
  rmsWeightedTotal: number;
  peakRms: number;
}

interface ActiveSegment {
  recorder: MediaRecorder;
  sequenceNumber: number;
  startedAt: Date;
  chunks: Blob[];
  metrics: SegmentMetrics;
  metricsClosed: boolean;
  stopPromise?: Promise<void>;
}

interface CalibrationState {
  generation: number;
  values: number[];
  window: RmsWindow;
  timer: ReturnType<typeof setTimeout>;
  resolve: (noiseFloor: number) => void;
}

interface PendingStart {
  generation: number;
  sessionId: string;
  operationIds: string[];
}

/**
 * Small surface used by RecorderEngine so its local-recording behavior can be
 * verified with capture and proxy doubles without a microphone or WebSocket.
 */
export interface AudioCaptureController {
  getSnapshot(): AudioCaptureSnapshot;
  setFrameHandler(
    handler?: (frame: CapturedAudioFrame) => void,
  ): void;
  subscribe(
    listener: (snapshot: AudioCaptureSnapshot) => void,
  ): () => void;
  start(stream: MediaStream): Promise<AudioCaptureSnapshot>;
  flush(): Promise<void>;
  pause(): Promise<void>;
  resume(): Promise<void>;
  stop(): Promise<void>;
  dispose(): Promise<void>;
}

export interface AudioStreamingController {
  getSnapshot(): AudioStreamingSnapshot;
  subscribe(
    listener: (snapshot: AudioStreamingSnapshot) => void,
  ): () => void;
  start(options: AudioStreamingStartOptions): void;
  enqueue(chunk: RawChunk): boolean;
  flush(): void;
  pause(): void;
  resume(): void;
  stop(): Promise<void>;
  dispose(): void;
}

export interface RecorderEngineOptions {
  createAudioCapture?: () => AudioCaptureController;
  createStreamingSession?: () => AudioStreamingController;
  calibrationDurationMs?: number;
}

const initialStreamingSnapshot: AudioStreamingSnapshot = {
  state: "idle",
  paused: false,
  queuedChunks: 0,
  queuedDurationMs: 0,
  nextSequenceNumber: 0,
  reconnectAttempt: 0,
  bufferedAmount: 0,
};

const initialSnapshot: RecorderSnapshot = {
  state: "idle",
  microphoneState: "not_requested",
  persistedChunkCount: 0,
  likelySilentCount: 0,
  selectedMimeType: "Browser default",
  streaming: initialStreamingSnapshot,
};

export class RecorderEngine {
  private readonly store: BrowserQueueStore;
  private readonly queue: UploadQueue;
  private readonly onChange: (snapshot: RecorderSnapshot) => void;
  private readonly createAudioCapture: () => AudioCaptureController;
  private readonly createStreamingSession: () => AudioStreamingController;
  private readonly calibrationDurationMs: number;
  private snapshot: RecorderSnapshot = initialSnapshot;
  private stream?: MediaStream;
  private track?: MediaStreamTrack;
  private audioCapture?: AudioCaptureController;
  private audioCaptureUnsubscribe?: () => void;
  private streamingSession?: AudioStreamingController;
  private streamingUnsubscribe?: () => void;
  private streamingChunker?: PcmChunker;
  private acceptStreamingFrames = false;
  private activeSegment?: ActiveSegment;
  private segmentTimer?: ReturnType<typeof setTimeout>;
  private calibration?: CalibrationState;
  private sequenceNumber = 0;
  private order = 0;
  private mimeType?: string;
  private disposed = false;
  private generation = 0;
  private pendingStart?: PendingStart;
  private trackEndedHandler = () => {
    void this.handleTrackEnded();
  };

  constructor(
    store: BrowserQueueStore,
    queue: UploadQueue,
    onChange: (snapshot: RecorderSnapshot) => void,
    options: RecorderEngineOptions = {},
  ) {
    this.store = store;
    this.queue = queue;
    this.onChange = onChange;
    this.createAudioCapture = options.createAudioCapture ?? (() => new AudioCapture());
    this.createStreamingSession =
      options.createStreamingSession ??
      (() =>
        new AudioStreamingSession({
          ...readAudioStreamingConfig(),
          logger: (event, fields) => this.logStreaming(event, fields),
        }));
    this.calibrationDurationMs =
      options.calibrationDurationMs ?? CALIBRATION_DURATION_MS;
  }

  getSnapshot(): RecorderSnapshot {
    return this.snapshot;
  }

  async start(deviceId?: string, proxySessionKey?: string): Promise<void> {
    if (
      !["idle", "completed", "error"].includes(this.snapshot.state) ||
      this.disposed
    ) {
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia || !globalThis.MediaRecorder) {
      throw new Error("This browser does not support microphone recording.");
    }

    const generation = ++this.generation;
    let pendingStart: PendingStart | undefined;
    this.update({
      ...initialSnapshot,
      streaming: { ...initialStreamingSnapshot },
      state: "calibrating",
      microphoneState: "calibrating",
    });

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraintsForDevice(deviceId),
      });
      if (generation !== this.generation || this.disposed) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      this.stream = stream;

      this.track = this.stream.getAudioTracks()[0];
      if (!this.track) throw new Error("No audio track was provided.");
      this.track.addEventListener("ended", this.trackEndedHandler);

      const sessionId = crypto.randomUUID();
      const captureSnapshot = await this.startPcmCapture(generation);
      if (generation !== this.generation || this.disposed) return;
      const noiseFloor = await this.calibrateFromPcm(generation, captureSnapshot);
      if (generation !== this.generation || this.disposed) return;

      const speechThreshold = Math.max(
        noiseFloor * 2.5,
        DEFAULT_SPEECH_THRESHOLD,
      );
      this.mimeType = selectSupportedMimeType((candidate) =>
        MediaRecorder.isTypeSupported(candidate),
      );
      const startedAt = new Date();
      this.sequenceNumber = 0;
      this.order = Date.now() * 1_000;

      const sessionOperation: CreateSessionOperation = {
        ...this.operationBase(sessionId),
        kind: "create_session",
        payload: {
          sessionId,
          startedAt: startedAt.toISOString(),
          mimeType: this.mimeType ?? "",
          noiseFloor,
          speechThreshold,
        },
      };

      const startedEvent = this.createEventOperation(
        sessionId,
        "started",
        startedAt,
      );

      pendingStart = {
        generation,
        sessionId,
        operationIds: [],
      };
      this.pendingStart = pendingStart;

      await this.store.putSessionState({
        sessionId,
        serverCreated: false,
        persistedChunkCount: 0,
        likelySilentCount: 0,
      });
      if (generation !== this.generation || this.disposed) {
        await this.abandonPendingStart(pendingStart);
        return;
      }
      pendingStart.operationIds.push(sessionOperation.id);
      await this.store.putOperation(sessionOperation);
      if (generation !== this.generation || this.disposed) {
        await this.abandonPendingStart(pendingStart);
        return;
      }
      pendingStart.operationIds.push(startedEvent.id);
      await this.store.putOperation(startedEvent);
      if (generation !== this.generation || this.disposed) {
        await this.abandonPendingStart(pendingStart);
        return;
      }
      if (this.pendingStart === pendingStart) this.pendingStart = undefined;
      pendingStart = undefined;

      this.update({
        state: "recording",
        microphoneState: "live",
        sessionId,
        sessionStartedAt: startedAt.getTime(),
        segmentStartedAt: undefined,
        persistedChunkCount: 0,
        likelySilentCount: 0,
        selectedMimeType: this.mimeType || "Browser default",
        noiseFloor,
        speechThreshold,
        streaming: this.snapshot.streaming,
        error: undefined,
      });

      this.startStreamingSidecar(
        sessionId,
        captureSnapshot,
        generation,
        proxySessionKey,
      );
      void this.queue.kick();
      await this.startSegment();
    } catch (error) {
      if (generation !== this.generation || this.disposed) {
        if (pendingStart) await this.abandonPendingStart(pendingStart);
        return;
      }
      if (pendingStart) await this.abandonPendingStart(pendingStart);
      await this.releaseMedia();
      const message =
        error instanceof Error ? error.message : "Unable to start recording.";
      this.update({
        ...this.snapshot,
        state: "error",
        microphoneState: "stopped",
        segmentStartedAt: undefined,
        error: message,
      });
      throw error;
    }
  }

  async mute(): Promise<void> {
    if (this.snapshot.state !== "recording" || !this.track) return;

    const mutedAt = new Date();
    // This must happen before waiting for the worklet or MediaRecorder to
    // flush, so no post-pause microphone samples are captured.
    this.freezeActiveSegmentMetrics();
    this.track.enabled = false;
    this.update({
      ...this.snapshot,
      state: "muted",
      microphoneState: "muted",
      segmentStartedAt: undefined,
    });

    try {
      // Finalize the durable recorder immediately. The asynchronous PCM
      // flush happens alongside it and must never extend blob timestamps.
      const segmentFinalization = this.finishCurrentSegment(mutedAt);
      const sidecarPause = this.pausePcmSidecar();
      await Promise.all([segmentFinalization, sidecarPause]);
      if (this.snapshot.sessionId) {
        await this.store.putOperation(
          this.createEventOperation(this.snapshot.sessionId, "muted", mutedAt),
        );
        void this.queue.kick();
      }
    } catch (error) {
      this.reportError(error);
      throw error;
    }
  }

  async unmute(): Promise<void> {
    if (this.snapshot.state !== "muted" || !this.track) return;

    const unmutedAt = new Date();
    this.track.enabled = true;
    try {
      await this.resumePcmSidecar();
      if (this.snapshot.sessionId) {
        await this.store.putOperation(
          this.createEventOperation(
            this.snapshot.sessionId,
            "unmuted",
            unmutedAt,
          ),
        );
      }

      this.update({
        ...this.snapshot,
        state: "recording",
        microphoneState: "live",
        error: undefined,
      });
      void this.queue.kick();
      await this.startSegment();
    } catch (error) {
      this.track.enabled = false;
      this.reportError(error);
      throw error;
    }
  }

  async stop(): Promise<void> {
    if (this.snapshot.state === "calibrating") {
      await this.cancelStartingSession();
      return;
    }

    if (
      ![
        "recording",
        "muted",
        "microphone_disconnected",
        "error",
      ].includes(this.snapshot.state)
    ) {
      return;
    }

    const stoppedAt = new Date();
    this.freezeActiveSegmentMetrics();
    if (this.track) this.track.enabled = false;
    this.update({
      ...this.snapshot,
      state: "stopping",
      segmentStartedAt: undefined,
    });

    try {
      // The sidecar drains separately, so its one-second bounded send wait
      // cannot extend the timestamp or Blob contents of this local segment.
      const segmentFinalization = this.finishCurrentSegment(stoppedAt);
      const sidecarStop = this.stopPcmSidecar();
      await Promise.all([segmentFinalization, sidecarStop]);
      if (this.snapshot.sessionId) {
        await this.store.putOperation(
          this.createEventOperation(
            this.snapshot.sessionId,
            "stopped",
            stoppedAt,
          ),
        );
        const completeOperation: CompleteSessionOperation = {
          ...this.operationBase(this.snapshot.sessionId),
          kind: "complete_session",
          payload: { endedAt: stoppedAt.toISOString() },
        };
        await this.store.putOperation(completeOperation);
      }

      await this.releaseMedia();
      this.update({
        ...this.snapshot,
        state: "completed",
        microphoneState: "stopped",
        segmentStartedAt: undefined,
      });
      void this.queue.kick();
    } catch (error) {
      this.reportError(error);
      await this.releaseMedia();
      throw error;
    }
  }

  /**
   * A user can stop while the initial noise calibration is still pending.
   * There is no durable session yet at that point, so invalidate the in-flight
   * start, close the microphone/capture resources, and return to a clean idle
   * state rather than creating an empty recording session.
   */
  private async cancelStartingSession(): Promise<void> {
    if (this.snapshot.state !== "calibrating") return;

    const pendingStart = this.pendingStart;
    this.generation += 1;
    this.clearSegmentTimer();
    this.cancelCalibration();
    this.freezeActiveSegmentMetrics();
    if (this.track) this.track.enabled = false;
    await this.releaseMedia();
    if (pendingStart) await this.abandonPendingStart(pendingStart);
    this.activeSegment = undefined;
    this.sequenceNumber = 0;
    this.order = 0;
    this.mimeType = undefined;
    this.update({
      ...initialSnapshot,
      streaming: { ...initialStreamingSnapshot },
    });
  }

  /** Remove local state from a start that was cancelled before it became live. */
  private async abandonPendingStart(pendingStart: PendingStart): Promise<void> {
    await Promise.allSettled([
      this.store.deleteSessionState(pendingStart.sessionId),
      ...pendingStart.operationIds.map((operationId) =>
        this.store.deleteOperation(operationId),
      ),
    ]);
    if (this.pendingStart === pendingStart) this.pendingStart = undefined;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.clearSegmentTimer();
    this.cancelCalibration();
    this.freezeActiveSegmentMetrics();
    if (this.track) {
      this.track.removeEventListener("ended", this.trackEndedHandler);
    }

    const pendingStart = this.pendingStart;
    void (async () => {
      try {
        const segmentFinalization = this.finishCurrentSegment(new Date());
        const sidecarStop = this.stopPcmSidecar();
        await Promise.all([segmentFinalization, sidecarStop]);
      } catch (error) {
        this.reportError(error);
      } finally {
        await this.releaseMedia();
        if (pendingStart) await this.abandonPendingStart(pendingStart);
        this.generation += 1;
      }
    })();
  }

  private async startPcmCapture(
    generation: number,
  ): Promise<AudioCaptureSnapshot> {
    if (!this.stream) {
      return {
        state: "unavailable",
        targetSampleRate: 16_000,
        error: "The microphone stream was not available for PCM capture.",
      };
    }

    const capture = this.createAudioCapture();
    this.audioCapture = capture;
    capture.setFrameHandler((frame) => this.handlePcmFrame(frame, generation));
    this.audioCaptureUnsubscribe = capture.subscribe((snapshot) => {
      this.handlePcmCaptureState(snapshot, generation);
    });
    const snapshot = await capture.start(this.stream);

    if (snapshot.state === "unavailable") {
      this.setStreamingUnavailable(
        snapshot.error ?? "PCM streaming is unavailable in this browser.",
      );
    }
    return snapshot;
  }

  private async calibrateFromPcm(
    generation: number,
    captureSnapshot: AudioCaptureSnapshot,
  ): Promise<number> {
    if (captureSnapshot.state !== "capturing") {
      return 0;
    }

    return new Promise<number>((resolve) => {
      const timer = setTimeout(() => {
        this.completeCalibration(generation);
      }, this.calibrationDurationMs);
      this.calibration = {
        generation,
        values: [],
        window: this.newRmsWindow(),
        timer,
        resolve,
      };
    });
  }

  private completeCalibration(generation: number): void {
    const calibration = this.calibration;
    if (!calibration || calibration.generation !== generation) return;
    clearTimeout(calibration.timer);
    this.calibration = undefined;
    this.flushCalibrationWindow(calibration);
    const noiseFloor =
      calibration.values.length > 0
        ? calibration.values.reduce((sum, value) => sum + value, 0) /
          calibration.values.length
        : 0;
    calibration.resolve(noiseFloor);
  }

  private cancelCalibration(): void {
    const calibration = this.calibration;
    if (!calibration) return;
    clearTimeout(calibration.timer);
    this.calibration = undefined;
    calibration.resolve(0);
  }

  private startStreamingSidecar(
    sessionId: string,
    captureSnapshot: AudioCaptureSnapshot,
    generation: number,
    proxySessionKey?: string,
  ): void {
    if (captureSnapshot.state !== "capturing") return;
    // AudioCapture performs this check before building its graph. Keep the
    // same guard here for injected capture implementations and future changes
    // so the browser can never open a proxy socket at a mismatched rate.
    if (captureSnapshot.actualSampleRate !== TARGET_SAMPLE_RATE) {
      this.setStreamingUnavailable(
        `PCM streaming requires a native ${TARGET_SAMPLE_RATE} Hz audio context.`,
      );
      return;
    }

    try {
      const streamingSession = this.createStreamingSession();
      this.streamingSession = streamingSession;
      this.streamingChunker = new PcmChunker();
      this.streamingUnsubscribe = streamingSession.subscribe((snapshot) => {
        if (
          generation !== this.generation ||
          this.streamingSession !== streamingSession
        ) {
          return;
        }
        this.update({ ...this.snapshot, streaming: snapshot });
      });
      streamingSession.start({
        // This is the durable D1/R2 recording ID. AudioStreamingSession keeps
        // it only for diagnostics/sessionStorage and deliberately omits it
        // from a first LongformAudioProxy handshake.
        sessionId,
        // A pilot credential is provided by the user at runtime. It never
        // enters the durable queue, recorder snapshot, or structured logs.
        sessionKey: proxySessionKey,
        actualSampleRate: captureSnapshot.actualSampleRate,
        actualChannelCount: captureSnapshot.inputChannelCount,
      });
      this.acceptStreamingFrames = this.canAcceptStreamingFrames(
        streamingSession.getSnapshot(),
      );
    } catch (error) {
      this.setStreamingUnavailable(
        error instanceof Error
          ? error.message
          : "Unable to start the audio streaming sidecar.",
      );
    }
  }

  private handlePcmCaptureState(
    captureSnapshot: AudioCaptureSnapshot,
    generation: number,
  ): void {
    if (generation !== this.generation) return;
    this.logStreaming("pcm_capture_state", {
      sessionId: this.snapshot.sessionId,
      state: this.snapshot.streaming.state,
      actualSampleRate: captureSnapshot.actualSampleRate,
      actualChannelCount: captureSnapshot.inputChannelCount,
      errorMessage: captureSnapshot.error,
    });

    if (captureSnapshot.state === "unavailable") {
      this.acceptStreamingFrames = false;
      this.streamingUnsubscribe?.();
      this.streamingUnsubscribe = undefined;
      this.streamingSession?.dispose();
      this.streamingSession = undefined;
      this.streamingChunker = undefined;
      this.setStreamingUnavailable(
        captureSnapshot.error ?? "PCM streaming is unavailable in this browser.",
      );
    }
  }

  private handlePcmFrame(
    frame: CapturedAudioFrame,
    generation: number,
  ): void {
    if (generation !== this.generation) return;

    const calibration = this.calibration;
    const durationMs = this.frameDurationMs(frame);
    if (calibration?.generation === generation && Number.isFinite(frame.rms)) {
      this.addFrameToCalibration(calibration, frame.rms, frame.peak, durationMs);
    }

    const segment = this.activeSegment;
    if (segment && !segment.metricsClosed) {
      const rms = Number.isFinite(frame.rms) ? frame.rms : 0;
      const peak = Number.isFinite(frame.peak) ? frame.peak : 0;
      segment.metrics.peakRms = Math.max(segment.metrics.peakRms, peak);
      this.addFrameToSegmentMetrics(segment, rms, peak, durationMs);
    }

    if (
      !this.acceptStreamingFrames ||
      !this.streamingChunker ||
      !this.streamingSession
    ) {
      return;
    }
    this.enqueueStreamingChunks(this.streamingChunker.addFrame(frame));
  }

  private enqueueStreamingChunks(chunks: RawChunk[]): void {
    const streamingSession = this.streamingSession;
    if (!streamingSession) return;
    for (const chunk of chunks) {
      if (!streamingSession.enqueue(chunk)) {
        if (!this.canAcceptStreamingFrames(streamingSession.getSnapshot())) {
          this.acceptStreamingFrames = false;
        }
        return;
      }
    }
  }

  private flushStreamingChunker(): void {
    const finalChunk = this.streamingChunker?.flush();
    if (finalChunk) this.enqueueStreamingChunks([finalChunk]);
    this.streamingSession?.flush();
  }

  private async pausePcmSidecar(): Promise<void> {
    try {
      // The proxy needs measured silence during a manual pause. The disabled
      // track continues feeding zero-valued PCM through the still-running
      // worklet and socket; this is intentionally only sidecar UI metadata.
      this.streamingSession?.pause();
      this.acceptStreamingFrames = this.canAcceptStreamingFrames(
        this.streamingSession?.getSnapshot(),
      );
    } catch (error) {
      this.logStreaming("pcm_pause_failed", {
        sessionId: this.snapshot.sessionId,
        state: this.snapshot.streaming.state,
        errorMessage: error instanceof Error ? error.message : "PCM pause failed.",
      });
    }
  }

  private async resumePcmSidecar(): Promise<void> {
    try {
      this.streamingSession?.resume();
      this.acceptStreamingFrames = this.canAcceptStreamingFrames(
        this.streamingSession?.getSnapshot(),
      );
    } catch (error) {
      this.logStreaming("pcm_resume_failed", {
        sessionId: this.snapshot.sessionId,
        state: this.snapshot.streaming.state,
        errorMessage:
          error instanceof Error ? error.message : "PCM resume failed.",
      });
    }
  }

  private async stopPcmSidecar(): Promise<void> {
    try {
      await this.audioCapture?.flush();
      this.flushStreamingChunker();
      this.acceptStreamingFrames = false;
      await this.streamingSession?.stop();
    } catch (error) {
      this.logStreaming("pcm_stop_failed", {
        sessionId: this.snapshot.sessionId,
        state: this.snapshot.streaming.state,
        errorMessage: error instanceof Error ? error.message : "PCM stop failed.",
      });
    }
  }

  private canAcceptStreamingFrames(
    snapshot: AudioStreamingSnapshot | undefined,
  ): boolean {
    return Boolean(
      snapshot &&
        !["unavailable", "degraded", "stopped"].includes(snapshot.state),
    );
  }

  private setStreamingUnavailable(error: string): void {
    this.update({
      ...this.snapshot,
      streaming: {
        ...this.snapshot.streaming,
        state: "unavailable",
        sessionId: this.snapshot.sessionId,
        paused: this.snapshot.state === "muted",
        error,
      },
    });
  }

  private async startSegment(): Promise<void> {
    if (
      this.snapshot.state !== "recording" ||
      !this.stream ||
      !this.snapshot.sessionId ||
      this.activeSegment
    ) {
      return;
    }

    const recorder = createSegmentRecorder(this.stream, this.mimeType);
    const startedAt = new Date();
    const segment: ActiveSegment = {
      recorder,
      sequenceNumber: ++this.sequenceNumber,
      startedAt,
      chunks: [],
      metrics: {
        speechMs: 0,
        peakRms: 0,
        rmsWeightedTotal: 0,
        rmsDurationMs: 0,
        window: this.newRmsWindow(),
      },
      metricsClosed: false,
    };

    recorder.addEventListener("dataavailable", (event: BlobEvent) => {
      segment.chunks.push(event.data);
    });
    this.activeSegment = segment;
    recorder.start();
    this.acceptStreamingFrames = this.canAcceptStreamingFrames(
      this.streamingSession?.getSnapshot(),
    );

    this.segmentTimer = setTimeout(() => {
      void this.rollSegment();
    }, SEGMENT_DURATION_MS);

    this.update({
      ...this.snapshot,
      segmentStartedAt: startedAt.getTime(),
    });
  }

  private async rollSegment(): Promise<void> {
    if (this.snapshot.state !== "recording") return;
    try {
      await this.finishCurrentSegment(new Date());
      if (this.snapshot.state === "recording") {
        await this.startSegment();
      }
    } catch (error) {
      this.reportError(error);
    }
  }

  private finishCurrentSegment(endedAt: Date): Promise<void> {
    const segment = this.activeSegment;
    if (!segment) return Promise.resolve();
    if (segment.stopPromise) return segment.stopPromise;

    this.freezeActiveSegmentMetrics();
    this.clearSegmentTimer();
    this.activeSegment = undefined;
    this.update({ ...this.snapshot, segmentStartedAt: undefined });

    segment.stopPromise = new Promise<void>((resolve, reject) => {
      let persistStarted = false;
      const persistOnce = () => {
        if (persistStarted) return;
        persistStarted = true;
        void this.persistSegment(segment, endedAt).then(resolve, reject);
      };
      segment.recorder.addEventListener(
        "error",
        (event) => {
          reject(
            new Error(
              event instanceof ErrorEvent
                ? event.message
                : "MediaRecorder failed while finalizing a segment.",
            ),
          );
        },
        { once: true },
      );
      segment.recorder.addEventListener("stop", persistOnce, { once: true });

      if (segment.recorder.state === "inactive") {
        persistOnce();
      } else {
        segment.recorder.stop();
      }
    });

    return segment.stopPromise;
  }

  private async persistSegment(
    segment: ActiveSegment,
    endedAt: Date,
  ): Promise<void> {
    if (!this.snapshot.sessionId) return;
    const contentType =
      segment.recorder.mimeType ||
      this.mimeType ||
      segment.chunks.find((chunk) => chunk.type)?.type ||
      "application/octet-stream";
    const blob = new Blob(segment.chunks, { type: contentType });
    const averageRms =
      segment.metrics.rmsDurationMs > 0
        ? segment.metrics.rmsWeightedTotal / segment.metrics.rmsDurationMs
        : 0;
    const speechMs = Math.round(segment.metrics.speechMs);
    const metadata: ChunkMetadata = {
      chunkId: crypto.randomUUID(),
      sequenceNumber: segment.sequenceNumber,
      startedAt: segment.startedAt.toISOString(),
      endedAt: endedAt.toISOString(),
      durationMs: Math.max(0, endedAt.getTime() - segment.startedAt.getTime()),
      speechMs,
      peakRms: segment.metrics.peakRms,
      averageRms,
      likelySilent: isLikelySilent(speechMs),
      contentType,
    };
    const operation: UploadChunkOperation = {
      ...this.operationBase(this.snapshot.sessionId),
      kind: "upload_chunk",
      metadata,
      blob,
    };

    try {
      await this.store.putOperation(operation);
      const localState = await this.store.recordPersistedChunk(
        this.snapshot.sessionId,
        metadata.likelySilent,
      );
      this.update({
        ...this.snapshot,
        persistedChunkCount: localState.persistedChunkCount,
        likelySilentCount: localState.likelySilentCount,
      });
      void this.queue.kick();
    } catch (error) {
      this.reportError(error);
      throw error;
    }
  }

  private createEventOperation(
    sessionId: string,
    type: SessionEventType,
    occurredAt: Date,
  ): CreateEventOperation {
    return {
      ...this.operationBase(sessionId),
      kind: "create_event",
      payload: {
        eventId: crypto.randomUUID(),
        type,
        occurredAt: occurredAt.toISOString(),
      },
    };
  }

  private operationBase(
    sessionId: string,
  ): Omit<
    QueueOperation,
    "kind" | "payload" | "metadata" | "blob"
  > & { id: string } {
    return {
      id: crypto.randomUUID(),
      sessionId,
      order: ++this.order,
      createdAt: Date.now(),
      retryCount: 0,
      nextAttemptAt: 0,
      state: "pending",
    };
  }

  private async handleTrackEnded(): Promise<void> {
    if (
      !["recording", "muted"].includes(this.snapshot.state) ||
      !this.snapshot.sessionId
    ) {
      return;
    }
    const endedAt = new Date();
    this.freezeActiveSegmentMetrics();
    this.update({
      ...this.snapshot,
      state: "microphone_disconnected",
      microphoneState: "disconnected",
      segmentStartedAt: undefined,
      error:
        "The microphone disconnected. Stop this session, then start a new session after reconnecting it.",
    });
    try {
      const segmentFinalization = this.finishCurrentSegment(endedAt);
      // An unexpected ended track cannot continue supplying measured silence,
      // so finalize the optional proxy session without changing the durable
      // recorder's microphone-disconnected state.
      const sidecarStop = this.stopPcmSidecar();
      await Promise.all([segmentFinalization, sidecarStop]);
      await this.store.putOperation(
        this.createEventOperation(
          this.snapshot.sessionId,
          "microphone_disconnected",
          endedAt,
        ),
      );
      void this.queue.kick();
    } catch (error) {
      this.reportError(error);
    }
  }

  private clearSegmentTimer(): void {
    if (this.segmentTimer) clearTimeout(this.segmentTimer);
    this.segmentTimer = undefined;
  }

  private freezeActiveSegmentMetrics(): void {
    const segment = this.activeSegment;
    if (!segment || segment.metricsClosed) return;
    this.flushSegmentRmsWindow(segment);
    segment.metricsClosed = true;
  }

  private frameDurationMs(frame: CapturedAudioFrame): number {
    const validSamples = Math.min(
      frame.samples.length,
      Math.max(0, frame.validSamples),
    );
    return frame.samples.length > 0
      ? (frame.durationMs * validSamples) / frame.samples.length
      : 0;
  }

  private newRmsWindow(): RmsWindow {
    return { durationMs: 0, rmsWeightedTotal: 0, peakRms: 0 };
  }

  private addFrameToCalibration(
    calibration: CalibrationState,
    rms: number,
    peak: number,
    durationMs: number,
  ): void {
    this.addFrameToWindow(calibration.window, rms, peak, durationMs);
    if (calibration.window.durationMs >= RMS_SAMPLE_INTERVAL_MS) {
      this.flushCalibrationWindow(calibration);
    }
  }

  private flushCalibrationWindow(calibration: CalibrationState): void {
    const window = calibration.window;
    if (window.durationMs <= 0) return;
    calibration.values.push(window.rmsWeightedTotal / window.durationMs);
    calibration.window = this.newRmsWindow();
  }

  private addFrameToSegmentMetrics(
    segment: ActiveSegment,
    rms: number,
    peak: number,
    durationMs: number,
  ): void {
    this.addFrameToWindow(segment.metrics.window, rms, peak, durationMs);
    if (segment.metrics.window.durationMs >= RMS_SAMPLE_INTERVAL_MS) {
      this.flushSegmentRmsWindow(segment);
    }
  }

  private flushSegmentRmsWindow(segment: ActiveSegment): void {
    const window = segment.metrics.window;
    if (window.durationMs <= 0) return;
    const averageRms = window.rmsWeightedTotal / window.durationMs;
    segment.metrics.rmsWeightedTotal += window.rmsWeightedTotal;
    segment.metrics.rmsDurationMs += window.durationMs;
    if (averageRms >= (this.snapshot.speechThreshold ?? DEFAULT_SPEECH_THRESHOLD)) {
      segment.metrics.speechMs += window.durationMs;
    }
    segment.metrics.window = this.newRmsWindow();
  }

  private addFrameToWindow(
    window: RmsWindow,
    rms: number,
    peak: number,
    durationMs: number,
  ): void {
    if (durationMs <= 0) return;
    window.durationMs += durationMs;
    window.rmsWeightedTotal += rms * durationMs;
    window.peakRms = Math.max(window.peakRms, peak);
  }

  private async releaseMedia(): Promise<void> {
    this.clearSegmentTimer();
    this.cancelCalibration();
    if (this.track) {
      this.track.removeEventListener("ended", this.trackEndedHandler);
    }

    this.acceptStreamingFrames = false;
    this.audioCaptureUnsubscribe?.();
    this.audioCaptureUnsubscribe = undefined;
    const capture = this.audioCapture;
    this.audioCapture = undefined;
    await capture?.dispose().catch(() => undefined);

    this.streamingUnsubscribe?.();
    this.streamingUnsubscribe = undefined;
    const streamingSession = this.streamingSession;
    this.streamingSession = undefined;
    this.streamingChunker = undefined;
    streamingSession?.dispose();

    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = undefined;
    this.track = undefined;
  }

  private reportError(error: unknown): void {
    const message =
      error instanceof Error ? error.message : "The recorder encountered an error.";
    if (this.track) this.track.enabled = false;
    this.update({
      ...this.snapshot,
      state: "error",
      microphoneState:
        this.snapshot.microphoneState === "disconnected"
          ? "disconnected"
          : "muted",
      segmentStartedAt: undefined,
      error: message,
    });
  }

  private logStreaming(
    event: string,
    fields: object,
  ): void {
    if (import.meta.env.DEV) {
      console.debug("[audio-streaming]", { event, ...fields });
    }
  }

  private update(snapshot: RecorderSnapshot): void {
    this.snapshot = snapshot;
    this.onChange(snapshot);
  }
}
